import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { fetchStories } from "@/lib/api";

function applyJournalIndexSEO(stories) {
    document.title =
      "Journal — perimenopause stories & essays | Supple";
    const setMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(
      "name",
      "description",
      "Long-form essays on perimenopause for Asian women — hormone health, healthcare gaps, and the science behind Supple's approach."
    );
    setMeta("property", "og:title", "Journal | Supple");
    setMeta("property", "og:url", "https://supple.care/journal");

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://supple.care/journal");

    // JSON-LD — ItemList of blog posts
    const graph = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "@id": "https://supple.care/journal",
      name: "Supple Journal",
      publisher: { "@type": "Organization", name: "Supple" },
      blogPost: stories.map((s) => ({
        "@type": "BlogPosting",
        headline: s.title,
        url: `https://supple.care${s.url_prefix || "/journal"}/${s.slug}`,
        datePublished: s.published_at,
        description: s.excerpt,
        image: s.hero_image?.startsWith("http")
          ? s.hero_image
          : `https://supple.care${s.hero_image}`,
        author: { "@type": "Person", name: "Shruti Gupta" },
      })),
    };
    let script = document.getElementById("journal-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "journal-jsonld";
      document.head.appendChild(script);
    }
  script.textContent = JSON.stringify(graph);
}

function useJournalIndexSEO(stories) {
  useEffect(() => applyJournalIndexSEO(stories), [stories]);
}

export default function JournalIndex() {
  const [stories, setStories] = useState([]);
  useJournalIndexSEO(stories);

  useEffect(() => {
    let active = true;
    fetchStories()
      .then((data) => {
        if (active) setStories(data);
      })
      .catch(() => {
        if (active) setStories([]);
      });
    return () => {
      active = false;
    };
  }, [setStories]);

  return (
    <div className="bg-bg text-[#1C1917]" data-testid="journal-index-page">
      <Header />
      <main className="pt-24 sm:pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <header className="max-w-2xl mb-12 sm:mb-16">
            <p className="eyebrow mb-4">The Supple Journal</p>
            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917]"
              style={{ lineHeight: 1.05 }}
              data-testid="journal-headline"
            >
              Perimenopause, <em>told honestly.</em>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#44403C] leading-relaxed">
              Long-form essays on what perimenopause actually looks like for
              Asian women — the science, the healthcare gaps, and the
              questions we&apos;re asking as we build.
            </p>
          </header>

          {stories.length === 0 ? (
            <p className="text-[#78716C]">Loading stories…</p>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {stories.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`${s.url_prefix || "/journal"}/${s.slug}`}
                    className="block supple-card overflow-hidden group"
                    style={{ padding: 0 }}
                    data-testid={`journal-card-${s.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={s.hero_image}
                        alt={s.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6 sm:p-7">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-[10.5px] font-semibold tracking-[0.18em] uppercase text-[#9A3F25]">
                          {s.tag}
                        </span>
                        <span className="text-[#A8A29E]">·</span>
                        <span className="text-xs text-[#78716C]">
                          {s.read_time}
                        </span>
                      </div>
                      <h2
                        className="font-display text-2xl sm:text-[1.75rem] font-medium text-[#1C1917]"
                        style={{ lineHeight: 1.15 }}
                      >
                        {s.title}
                      </h2>
                      <p className="mt-3 text-[#44403C] leading-relaxed text-[15px] line-clamp-3">
                        {s.excerpt}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-[#9A3F25] font-semibold text-sm">
                        Read story <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-16 text-center">
            <Link
              to="/why"
              className="text-sm text-[#9A3F25] font-semibold border-b border-[#9A3F25]/30 hover:border-[#9A3F25] transition-colors"
              data-testid="journal-to-why-link"
            >
              Read why we&apos;re building Supple →
            </Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
